import { ChangeEvent, FC, memo, useCallback, useMemo } from 'react';
import Icon from '../Icon';
import Typography from '../Typography';
import './ToggleSwitch.scss';

export interface ToggleSwitchProps {
  checked: boolean;
  locked?: boolean;
  pending?: boolean;
  label?: string;
  disabled?: boolean;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
}

let idCounter = 0;

const ToggleSwitchComponent: FC<ToggleSwitchProps> = props => {
  const { checked, locked, pending, label, disabled, onChange } = props;

  const id = useMemo(() => {
    idCounter += 1;
    return `toggle-switch-${idCounter}`;
  }, []);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (!locked) {
        onChange?.(event);
      }
    },
    [locked, onChange],
  );

  return (
    <div className="nostra__toggleSwitch">
      <span
        className={`nostra__toggleSwitch__switchContainer 
        ${disabled ? 'nostra__toggleSwitch__switchContainerDisabled' : ''} 
        ${locked ? 'nostra__toggleSwitch__switchContainerLocked' : ''} 
        ${pending ? 'nostra__toggleSwitch__switchContainerPending' : ''}`}
      >
        {pending ? (
          <span className="nostra__toggleSwitch__background nostra__toggleSwitch__backgroundPending">
            <Icon variant="loading2" size={16} color="--icon-default" />
          </span>
        ) : (
          <>
            <input id={id} checked={checked} disabled={disabled} type="checkbox" onChange={handleChange} />
            <span
              className={`nostra__toggleSwitch__thumb ${disabled ? 'nostra__toggleSwitch__thumbDisabled' : ''} ${
                checked ? 'nostra__toggleSwitch__thumbActive' : ''
              }`}
            >
              {locked && <Icon variant="lock" size={10} color="--icon-default" />}
            </span>
            <span
              data-locked={locked}
              className={`nostra__toggleSwitch__background ${
                disabled || pending ? 'nostra__toggleSwitch__backgroundDisabled' : ''
              } ${checked ? 'nostra__toggleSwitch__backgroundActive' : ''}`}
            />
          </>
        )}
      </span>
      {label && (
        <label htmlFor={id} className="nostra__toggleSwitch__label">
          <Typography variant="body-sm">{label}</Typography>
        </label>
      )}
    </div>
  );
};

export const ToggleSwitch = memo(ToggleSwitchComponent);
